import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Item } from '../types/item.types';
import { itemService } from '../services/item.service';
import ItemBatchesSection from './ItemBatchesSection';
import ItemLocationsSection from './ItemLocationsSection';

const ItemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [item, setItem] = useState<Item | null>(null);
  
  // Format date to a readable format
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // Format currency
  const formatCurrency = (amount?: number | null) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };
  
  // Fetch item data
  useEffect(() => {
    const fetchItem = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await itemService.getItemById(id);
        setItem(data);
      } catch (error: any) {
        console.error('Error fetching item:', error);
        enqueueSnackbar(
          error.message || 'Failed to load item data',
          { variant: 'error' }
        );
        navigate('/settings/items');
      } finally {
        setLoading(false);
      }
    };
    
    fetchItem();
  }, [id, navigate, enqueueSnackbar]);
  
  // Handle edit
  const handleEdit = () => {
    if (item) {
      navigate(`/settings/items/${item.id}/edit`);
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    if (!id) return;
    
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        setSaving(true);
        await itemService.deleteItem(id);
        enqueueSnackbar('Item deleted successfully', { variant: 'success' });
        navigate('/settings/items');
      } catch (error: any) {
        console.error('Error deleting item:', error);
        enqueueSnackbar(
          error.message || 'Failed to delete item',
          { variant: 'error' }
        );
      } finally {
        setSaving(false);
      }
    }
  };
  
  // Handle status toggle
  const handleToggleStatus = async () => {
    if (!id || !item) return;
    
    try {
      setSaving(true);
      const updatedItem = await itemService.toggleItemStatus(id, !item.isActive);
      setItem(updatedItem);
      enqueueSnackbar(
        `Item ${updatedItem.isActive ? 'activated' : 'deactivated'} successfully`,
        { variant: 'success' }
      );
    } catch (error: any) {
      console.error('Error toggling item status:', error);
      enqueueSnackbar(
        error.message || 'Failed to update item status',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };
  
  // Handle refresh
  const handleRefresh = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await itemService.getItemById(id);
      setItem(data);
      enqueueSnackbar('Item data refreshed', { variant: 'success' });
    } catch (error: any) {
      console.error('Error refreshing item:', error);
      enqueueSnackbar(
        error.message || 'Failed to refresh item data',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading || !item) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/settings/items')}
          color="inherit"
        >
          Back to Items
        </Button>
        
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={saving} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            disabled={saving}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={saving}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center">
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                {item.itemName}
              </Typography>
              <Chip
                label={item.isActive ? 'Active' : 'Inactive'}
                color={item.isActive ? 'success' : 'default'}
                size="small"
                sx={{ ml: 2, fontWeight: 'medium' }}
              />
              <Chip
                label={item.itemType}
                color={item.itemType === 'INVENTORY' ? 'primary' : 'secondary'}
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            </Box>
          }
          subheader={
            <Box>
              <Typography variant="body2" color="textSecondary">
                {item.itemCode} • {item.itemGroup}
              </Typography>
              {item.description && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  {item.description}
                </Typography>
              )}
            </Box>
          }
          action={
            <Button
              variant={item.isActive ? 'outlined' : 'contained'}
              color={item.isActive ? 'error' : 'success'}
              startIcon={item.isActive ? <CancelIcon /> : <CheckCircleIcon />}
              onClick={handleToggleStatus}
              disabled={saving}
              size="small"
              sx={{ mt: 1, mr: 1 }}
            >
              {item.isActive ? 'Deactivate' : 'Activate'}
            </Button>
          }
        />
        
        <Divider />
        
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Item Code
                    </Typography>
                    <Typography variant="body1">
                      {item.itemCode}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Item Type
                    </Typography>
                    <Typography variant="body1">
                      {item.itemType}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Item Group
                    </Typography>
                    <Typography variant="body1">
                      {item.itemGroup}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Item Sub Group
                    </Typography>
                    <Typography variant="body1">
                      {item.itemSubGroup ? item.itemSubGroup.replace('_', '/') : 'None'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Unit of Measure
                    </Typography>
                    <Typography variant="body1">
                      {item.unitOfMeasure || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Reorder Level
                    </Typography>
                    <Typography variant="body1">
                      {item.reorderLevel !== null ? item.reorderLevel : 'N/A'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Status
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: item.isActive ? 'success.main' : 'text.disabled',
                          mr: 1,
                        }}
                      />
                      <Typography variant="body1">
                        {item.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Pricing
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Cost Price
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(item.costPrice || 0)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Selling Price
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(item.sellingPrice || 0)}
                    </Typography>
                  </Grid>
                  
                  {item.costPrice !== null && item.sellingPrice !== null && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Profit Margin
                            </Typography>
                            <Typography variant="body1">
                              {item.costPrice && item.sellingPrice
                                ? `${(
                                    ((item.sellingPrice - item.costPrice) / item.costPrice) * 100
                                  ).toFixed(2)}%`
                                : 'N/A'}
                            </Typography>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Profit Amount
                            </Typography>
                            <Typography variant="body1">
                              {item.costPrice !== null && item.sellingPrice !== null && item.sellingPrice !== undefined
                                ? formatCurrency((item.sellingPrice ?? 0) - (item.costPrice || 0))
                                : 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
            
            {/* Item Batches Section - Only show for INVENTORY items */}
            {item.itemType === 'INVENTORY' && (
              <Grid item xs={12}>
                <ItemBatchesSection 
                  itemId={item.id} 
                  itemName={item.itemName} 
                />
              </Grid>
            )}
            
            {/* Item Locations Section - Only show for INVENTORY items */}
            {item.itemType === 'INVENTORY' && (
              <Grid item xs={12}>
                <ItemLocationsSection 
                  itemId={item.id} 
                  itemName={item.itemName} 
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Audit Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Created By
                    </Typography>
                    <Typography variant="body1">
                      {item.createdBy || 'System'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                      Created At
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(item.createdAt)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      Last Updated By
                    </Typography>
                    <Typography variant="body1">
                      {item.updatedBy || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                      Last Updated At
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(item.updatedAt)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
        
        <Box p={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => navigate('/settings/items')}
            sx={{ mr: 1 }}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Edit Item
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default ItemDetail;
